import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { useCallback } from "react";
import {
  createCustomEvent,
  useFireCustomEvent,
} from "@adsk/acc-issues-core/providers/CustomEventContext";
import { IssueFailureInfo, CustomEventType } from "@adsk/acc-issues-types";
import { bulkDeleteIssuesAction } from "../../exports/redux";

type deleteIssueFnType = ({
  issueUids,
  comment,
  onSuccess,
  onPartialFailure,
  onFailure,
  shouldDeleteMarkups,
}: {
  issueUids: string[];
  comment: string;
  onSuccess?: () => void;
  onPartialFailure?: (failureInfo: IssueFailureInfo[]) => void;
  onFailure?: () => void;
  shouldDeleteMarkups: boolean;
}) => Promise<void>;

/**
 * Returns a function that deletes an issue.
 * @param issueUids - the IDs of the issues to delete.
 * @param comment - deletation comment.
 * @param onSuccess - a custom onSuccess handler.
 * @param onPartialFailure - a custom onPartialFailure handler.
 * @param onFailure - a custom onFailure handler.
 * @param shouldDeleteMarkups - controls if the markups should be deleted along side with the issues.
 */
export const useBatchDeleteIssues = (): deleteIssueFnType => {
  const dispatch = useDispatch();
  const fireCustomEvent = useFireCustomEvent();

  return useCallback(
    async ({
      issueUids,
      comment,
      onSuccess,
      onPartialFailure,
      onFailure,
      shouldDeleteMarkups,
    }) => {
      await dispatch(
        bulkDeleteIssuesAction({
          issueUids,
          comment,
          onSuccess,
          onPartialFailure,
          onFailure,
          shouldDeleteMarkups,
        })
      );

      const customEventData = createCustomEvent({
        type: CustomEventType.IssueDeleted,
        target: null,
        data: { issueIds: issueUids },
      });
      if (fireCustomEvent) {
        fireCustomEvent(customEventData);
      }
    },
    [dispatch, fireCustomEvent]
  );
};
